/**
 * Catering pilot (owner 2026-10-30): the ONE set of words and decisions every screen uses for LA Mirage's own TEST
 * booking (`catering_billing_plans.is_pilot`) and its TEST invoices (`tax_invoices.is_test`, KTS-/LTS- series).
 * Used by the partner portal, the couple portal, the client invoices page and the desk.
 *
 * 🔴 A row is a test ONLY when the server says so with an explicit `true`. Absent, false, null or an older server all
 * mean a REAL row, which renders exactly as it did before the pilot — this never guesses that something is a test,
 * and never guesses that something is NOT money.
 *
 * Pure: no React, no DOM, so it is pinned by a node-environment vitest.
 */

export const TEST_BADGE_LABEL = 'TEST';
/** The desk's label on a pilot plan panel. */
export const PILOT_PLAN_LABEL = 'PILOT / TEST';
/** Partner portal: the one-line note wherever a pilot booking is shown in detail. */
export const PILOT_PARTNER_NOTE = 'Test booking by LA Mirage — not a real event. Do not prepare food.';
/** Couple / client: the note beside a test invoice's real amount. */
export const TEST_INVOICE_NOTE = 'TEST invoice — pay ₹10 to test';
/** Couple / client: the Pay button on a test invoice (its link is a fixed ₹10 one). */
export const TEST_PAY_LABEL = 'Pay ₹10 (test)';
/** Desk: the note beside a test invoice — not money, not in any total. */
/** Partner Money: the note beside a TEST fee invoice's real amount and its ₹10 Pay button (T12 item 8). */
export const TEST_PAY_PARTNER_NOTE = 'Test payment ₹10 — real amount shown for checking only';
export const TEST_INVOICE_DESK_NOTE = 'TEST invoice — pay link takes ₹10; not counted in totals.';

export interface TestFlags { isTest?: boolean | null; isPilot?: boolean | null }

/** True only for an explicit `isTest: true` (a TEST invoice). */
export function isTestInvoice(x: TestFlags | null | undefined): boolean {
  return !!x && x.isTest === true;
}

/** True only for an explicit `isPilot: true` (a pilot plan / booking). */
export function isPilotPlan(x: TestFlags | null | undefined): boolean {
  return !!x && x.isPilot === true;
}

/** True for a pilot booking OR a TEST invoice — the "show the TEST badge" decision. */
export function isTestRow(x: TestFlags | null | undefined): boolean {
  return isPilotPlan(x) || isTestInvoice(x);
}

/** The Pay button label: TEST_PAY_LABEL on a test invoice, else the caller's normal label unchanged. */
export function payLabelFor(x: TestFlags | null | undefined, normal: string): string {
  return isTestInvoice(x) ? TEST_PAY_LABEL : normal;
}

/** The rows that count as money: every row except an explicit TEST invoice. */
export function withoutTestInvoices<T extends TestFlags>(list: T[] | null | undefined): T[] {
  return Array.isArray(list) ? list.filter((i) => !isTestInvoice(i)) : [];
}
